import { useEffect, useState } from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { formatCurrency, formatInteger, formatPercent } from "../../../../../formatters";
import { BrandReportAttemptRow } from "../../../../../Brands/brandsApiMapping";
import MobileTablePagination from "../../../../MobileTablePagination";

const COLUMNS: Array<{ key: keyof BrandReportAttemptRow; label: string; format: (row: BrandReportAttemptRow) => string }> = [
  { key: "attempts", label: "Report attempts", format: (r) => formatInteger(r.attempts) },
  { key: "reportedSuccess", label: "Reported success", format: (r) => formatInteger(r.reportedSuccess) },
  { key: "successRate", label: "Success rate", format: (r) => formatPercent(r.successRate * 100) },
  { key: "reportedOther", label: "Reported to other brand", format: (r) => formatInteger(r.reportedOther) },
  { key: "utilization", label: "Utilization rate", format: (r) => formatPercent(r.utilization * 100) },
  { key: "notReceived", label: "Not received", format: (r) => formatInteger(r.notReceived) },
  { key: "incomePerAttempt", label: "Income / attempt", format: (r) => formatCurrency(r.incomePerAttempt) },
];

const BRAND_COL_WIDTH = 150;
const DATA_COL_WIDTH = 100;
const GRID_TEMPLATE = `${BRAND_COL_WIDTH}px repeat(${COLUMNS.length}, ${DATA_COL_WIDTH}px)`;
const DEFAULT_PAGE_SIZE = 10;

interface BrandsReportAttemptTableProps {
  rows: BrandReportAttemptRow[];
  periodLabel: string;
}

// Mobile's replacement for the desktop BrandsReportAttemptTable.tsx - same
// columns/formatting, sticky Brand column, paginated instead of rendering
// every row at once (mirrors BrandsMonthlyTable.tsx's mobile pattern).
export default function BrandsReportAttemptTable({ rows, periodLabel }: BrandsReportAttemptTableProps) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = rows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <Paper variant="outlined" sx={{ p: "16px 14px", borderColor: "#ececec" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#1a1a1a", mb: 1.5 }}>
        Brand Reporting Attempt Analysis — {periodLabel}
      </Typography>

      <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", border: "1px solid #ececec", borderRadius: "10px" }}>
        <Box sx={{ minWidth: BRAND_COL_WIDTH + COLUMNS.length * DATA_COL_WIDTH }}>
          <Box sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #ececec" }}>
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 2,
                p: "11px 12px",
                fontSize: 11,
                fontWeight: 700,
                color: "#8E24AA",
                backgroundColor: "#faf8fb",
                borderRight: "1px solid #f0e8f3",
              }}
            >
              Brand
            </Box>
            {COLUMNS.map((column) => (
              <Box
                key={column.key}
                sx={{ p: "11px 8px", fontSize: 11, fontWeight: 700, color: "#8E24AA", textAlign: "right", backgroundColor: "#faf8fb" }}
              >
                {column.label}
              </Box>
            ))}
          </Box>

          {rows.length === 0
            ? Array.from({ length: 2 }).map((_, index) => (
                <Box key={index} sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #f4f4f4" }}>
                  <Box sx={{ position: "sticky", left: 0, backgroundColor: "#fff", p: "11px 12px" }}>
                    <Skeleton variant="text" width="70%" />
                  </Box>
                  {COLUMNS.map((column) => (
                    <Box key={column.key} sx={{ p: "11px 8px" }}>
                      <Skeleton variant="text" />
                    </Box>
                  ))}
                </Box>
              ))
            : pagedRows.map((row) => (
                <Box key={row.brand} sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #f4f4f4" }}>
                  <Box
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: "#fff",
                      borderRight: "1px solid #f4f4f4",
                      p: "11px 12px",
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: "#212121",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.brand}
                  </Box>
                  {COLUMNS.map((column) => (
                    <Box key={column.key} sx={{ p: "11px 8px", fontSize: 12.5, textAlign: "right", color: "#616161" }}>
                      {column.format(row)}
                    </Box>
                  ))}
                </Box>
              ))}
        </Box>
      </Box>

      {rows.length > 0 && (
        <MobileTablePagination
          total={rows.length}
          pageSize={pageSize}
          page={safePage}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </Paper>
  );
}
